import { api } from "../../api"

import { makeChatApiEndpoints } from "./endpoints"

import { makeChatViewModel, ChatViewModel } from "./chat"
import { makeMessageViewModel, MessageViewModel } from "./message"

import {
  ChatParticipantResponse,
  ChatReadResponse,
  ChatReadStateResponse,
  ChatResponse,
  ChatType,
  UnreadSummaryResponse,
} from "./chat-response"

import { MessageMention, MessageResponse, MessageType } from "./message-response"

const endpoints = makeChatApiEndpoints()

export const chatApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getChats: builder.query<ChatViewModel[], GetChatsQuery>({
      query: ({ orgId, member, workspace, search, limit, before }) => ({
        method: "GET",
        url: endpoints.getChats(),
        params: {
          orgId,
          member,
          workspace,
          search,
          limit,
          before,
        },
      }),

      transformResponse: (chats: ChatResponse[]) => chats.map(makeChatViewModel),

      providesTags: ["Chat"],
    }),
    getUnreadSummary: builder.query<UnreadSummaryResponse, GetUnreadSummaryQuery>({
      query: ({ orgId }) => ({
        method: "GET",
        url: endpoints.getUnreadSummary(),
        params: { orgId },
      }),

      transformResponse: (summary: UnreadSummaryResponse) => summary,

      providesTags: ["Chat", "ChatUnread"],
    }),
    getChatByEntity: builder.query<
      ChatViewModel,
      {
        type: "project" | "task"
        entityId: string
      }
    >({
      query: ({ type, entityId }) => ({
        url: endpoints.getChatByEntity(type, entityId),
      }),

      transformResponse: (chat: ChatResponse) => makeChatViewModel(chat),

      providesTags: ["Chat"],
    }),
    createChat: builder.mutation<ChatViewModel, CreateChatData>({
      query: (body) => ({
        method: "POST",
        url: endpoints.createChat(),
        body,
      }),

      transformResponse: ({ data }: { data: ChatResponse }) => makeChatViewModel(data),

      invalidatesTags: ["Chat"],
    }),

    updateChat: builder.mutation<ChatViewModel, UpdateChatData>({
      query: ({ chatId, ...body }) => ({
        method: "PUT",
        url: endpoints.updateChat(chatId),
        body,
      }),

      transformResponse: ({ data }: { data: ChatResponse }) => makeChatViewModel(data),

      invalidatesTags: ["Chat"],
    }),

    deleteChat: builder.mutation<void, { chatId: string }>({
      query: ({ chatId }) => ({
        method: "DELETE",
        url: endpoints.deleteChat(chatId),
      }),

      invalidatesTags: (result, error, { chatId }) => ["Chat", { type: "Message", id: chatId }],
    }),

    getMessages: builder.query<MessageViewModel[], GetMessagesQuery>({
      query: ({ chatId, limit, before }) => ({
        method: "GET",
        url: endpoints.getMessages(chatId),
        params: {
          limit,
          before,
        },
      }),

      transformResponse: (messages: MessageResponse[]) => messages.map(makeMessageViewModel),

      providesTags: (result, error, { chatId }) => [{ type: "Message", id: chatId }],
    }),

    sendMessage: builder.mutation<MessageViewModel, SendMessageData>({
      query: ({ chatId, ...body }) => ({
        method: "POST",
        url: endpoints.sendMessage(chatId),
        body,
      }),

      transformResponse: ({ data }: { data: MessageResponse }) => makeMessageViewModel(data),

      invalidatesTags: (result, error, { chatId }) => [{ type: "Message", id: chatId }, "Chat"],
    }),

    updateMessage: builder.mutation<MessageViewModel, UpdateMessageData>({
      query: ({ chatId, messageId, ...body }) => ({
        method: "PATCH",
        url: endpoints.updateMessage(chatId, messageId),
        body,
      }),

      transformResponse: ({ data }: { data: MessageResponse }) => makeMessageViewModel(data),

      invalidatesTags: (result, error, { chatId }) => [{ type: "Message", id: chatId }],
    }),

    getPinnedMessages: builder.query<MessageViewModel[], { chatId: string }>({
      query: ({ chatId }) => ({
        method: "GET",
        url: endpoints.getPinnedMessages(chatId),
      }),

      transformResponse: (messages: MessageResponse[]) => messages.map(makeMessageViewModel),
      providesTags: (result, error, { chatId }) => [{ type: "Message", id: chatId }],
    }),

    getChatReadStates: builder.query<ChatReadStateResponse[], { chatId: string }>({
      query: ({ chatId }) => ({
        method: "GET",
        url: endpoints.getChatReadStates(chatId),
      }),

      // Deliberately not the Message tag: this is kept current by the chat:read socket payload,
      // and sharing a tag with the messages meant every new message refetched it too.
      providesTags: (result, error, { chatId }) => [{ type: "ChatRead", id: chatId }],
    }),

    getChatParticipants: builder.query<ChatParticipantResponse[], { chatId: string }>({
      query: ({ chatId }) => ({
        method: "GET",
        url: endpoints.getChatParticipants(chatId),
      }),

      providesTags: ["Chat"],
    }),

    searchMessages: builder.query<MessageViewModel[], SearchMessagesQuery>({
      query: ({ chatId, query }) => ({
        method: "GET",
        url: endpoints.searchMessages(chatId),
        params: { q: query },
      }),

      transformResponse: (messages: MessageResponse[]) => messages.map(makeMessageViewModel),

      // Untagged, a repeated search served stale cache for 60s.
      providesTags: (result, error, { chatId }) => [{ type: "Message", id: chatId }],
    }),

    getMessagePosition: builder.query<MessagePosition, { chatId: string; messageId: string }>({
      query: ({ chatId, messageId }) => ({
        method: "GET",
        url: endpoints.getMessagePosition(chatId, messageId),
      }),

      transformResponse: (result: MessagePosition) => result,
    }),

    setMessagePinned: builder.mutation<MessageViewModel, SetMessagePinnedData>({
      query: ({ chatId, messageId, ...body }) => ({
        method: "PATCH",
        url: endpoints.setMessagePinned(chatId, messageId),
        body,
      }),

      transformResponse: ({ data }: { data: MessageResponse }) => makeMessageViewModel(data),

      invalidatesTags: (result, error, { chatId }) => [{ type: "Message", id: chatId }],
    }),

    setMessageDeleted: builder.mutation<MessageViewModel, SetMessageDeletedData>({
      query: ({ chatId, messageId, ...body }) => ({
        method: "PATCH",
        url: endpoints.setMessageDeleted(chatId, messageId),
        body,
      }),

      transformResponse: ({ data }: { data: MessageResponse }) => makeMessageViewModel(data),

      // Deleting changes what counts as unread, so the chat list is stale as well.
      invalidatesTags: (result, error, { chatId }) => [{ type: "Message", id: chatId }, "Chat"],
    }),

    setMessageReaction: builder.mutation<MessageViewModel, SetMessageReactionData>({
      query: ({ chatId, messageId, ...body }) => ({
        method: "PATCH",
        url: endpoints.setMessageReaction(chatId, messageId),
        body,
      }),

      transformResponse: ({ data }: { data: MessageResponse }) => makeMessageViewModel(data),

      invalidatesTags: (result, error, { chatId }) => [{ type: "Message", id: chatId }],
    }),

    markChatAsRead: builder.mutation<ChatReadResponse, { chatId: string }>({
      query: ({ chatId }) => ({
        method: "PATCH",
        url: endpoints.markChatAsRead(chatId),
      }),

      transformResponse: (chatRead: ChatReadResponse) => chatRead,

      invalidatesTags: ["ChatUnread"],

      // This fires on every chat open and on every message arriving in an open chat. Invalidating
      // "Chat" made each of those refetch every chat list, every participant list and the unread
      // summary, which is the traffic that made opening a chat slow. The only thing that actually
      // changes is this chat's own count, so patch it in place.
      onQueryStarted: ({ chatId }, { dispatch, getState, queryFulfilled }) => {
        const clearUnread = (chat?: ChatViewModel) => {
          if (chat?.id === chatId) chat.unreadCount = 0
        }

        const patches = chatApi.util.selectInvalidatedBy(getState(), ["Chat"]).map(({ endpointName, originalArgs }) => {
          if (endpointName === "getChats")
            return dispatch(
              chatApi.util.updateQueryData("getChats", originalArgs, (chats) => chats.forEach(clearUnread))
            )

          if (endpointName === "getChatByEntity")
            return dispatch(chatApi.util.updateQueryData("getChatByEntity", originalArgs, clearUnread))

          return undefined
        })

        queryFulfilled.catch(() => patches.forEach((patch) => patch?.undo()))
      },
    }),

    muteChat: builder.mutation<void, { chatId: string; muted: boolean }>({
      query: ({ chatId, muted }) => ({
        method: "PATCH",
        url: endpoints.muteChat(chatId),
        body: { muted },
      }),

      invalidatesTags: ["Chat"],
    }),
  }),
})

export type CreateChatData = {
  type: ChatType
  entityId?: string
  name?: string
  orgId: string
  participantIds?: string[]
  createdBy: string
}

export type SendMessageData = {
  chatId: string
  senderId: string
  type?: MessageType
  content: string
  replyTo?: string
  attachments?: string[]
  mentions?: MessageMention[]
}

export type UpdateMessageData = {
  chatId: string
  messageId: string
  content: string
}

export type SetMessagePinnedData = {
  chatId: string
  messageId: string
  pinned: boolean
}

export type SetMessageDeletedData = {
  chatId: string
  messageId: string
  deleted: boolean
}

export type SetMessageReactionData = {
  chatId: string
  messageId: string
  emoji: string
  reacted: boolean
}

export type GetChatsQuery = {
  orgId: string
  member?: string
  workspace?: string
  search?: string
  limit?: number
  before?: string
}

export type GetUnreadSummaryQuery = {
  orgId: string
}

export type SearchMessagesQuery = {
  chatId: string
  query: string
}

export type MessagePosition = {
  position: number
  createdAt: string
}

export type GetMessagesQuery = {
  chatId: string
  limit?: number
  before?: string
}

export type UpdateChatData = {
  chatId: string
  name?: string
  participantIds?: string[]
}

export const {
  useGetChatsQuery,
  useGetUnreadSummaryQuery,
  useGetChatByEntityQuery,
  useCreateChatMutation,
  useGetMessagesQuery,
  useSendMessageMutation,
  useUpdateChatMutation,
  useDeleteChatMutation,
  useUpdateMessageMutation,
  useGetPinnedMessagesQuery,
  useGetChatReadStatesQuery,
  useGetChatParticipantsQuery,
  useSearchMessagesQuery,
  useLazyGetMessagePositionQuery,
  useSetMessagePinnedMutation,
  useSetMessageDeletedMutation,
  useSetMessageReactionMutation,
  useMarkChatAsReadMutation,
  useMuteChatMutation,
} = chatApi
